const MIN_MARKER_PATH_LENGTH = 1;
const MIN_MARKER_STROKE_WIDTH = 0.65;

const MARKER_STROKE_PASSES = Object.freeze([
  Object.freeze({ opacity: 0.2, passOffset: 0, widthScale: 1.2 }),
  Object.freeze({ opacity: 0.84, passOffset: 0, widthScale: 0.92 }),
  Object.freeze({
    depositRatio: 0.13,
    opacity: 0.48,
    passOffset: 0.35,
    widthScale: 0.58,
  }),
  Object.freeze({
    depositRatio: 0.075,
    opacity: 0.4,
    passOffset: 0.7,
    widthScale: 0.4,
  }),
]);

/**
 * @param {string} id
 * @returns {number} order-sensitive deterministic seed for stable ink texture
 */
export const getElementSeed = (id = "") => {
  let hash = 0x811c9dc5;
  Array.from(String(id)).forEach((character) => {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193);
  });
  return hash >>> 0;
};

export const getMarkerUnitNoise = ({ index, seed }) => {
  const indexedSeed = Math.imul(Math.round(index * 1000), 0x9e3779b1);
  let value = (seed ^ indexedSeed) >>> 0;
  value = Math.imul(value ^ (value >>> 16), 0x85ebca6b);
  value = Math.imul(value ^ (value >>> 13), 0xc2b2ae35);
  return ((value ^ (value >>> 16)) >>> 0) / 0xffffffff;
};

export const getMarkerJitter = ({ index, scale, seed }) =>
  (getMarkerUnitNoise({ index, seed }) - 0.5) * scale;

const getDepositStyle = ({ pass, passIndex, pathLength, seed }) => {
  if (!pass.depositRatio) return { dash: [], dashOffset: 0 };

  const safeLength = Math.max(pathLength, MIN_MARKER_PATH_LENGTH);
  const depositLength = safeLength * pass.depositRatio;
  const startFraction = getMarkerUnitNoise({ index: 101 + passIndex, seed });
  return {
    dash: [depositLength, safeLength - depositLength],
    dashOffset: -(safeLength * startFraction),
  };
};

const paintMarkerPass = ({ baseAlpha, ctx, pass, passIndex, params }) => {
  const { pathLength, seed, strokeWidth, tracePath } = params;
  const deposit = getDepositStyle({ pass, passIndex, pathLength, seed });
  ctx.beginPath();
  tracePath(pass.passOffset);
  ctx.globalAlpha = baseAlpha * pass.opacity;
  ctx.lineWidth = Math.max(MIN_MARKER_STROKE_WIDTH, strokeWidth * pass.widthScale);
  ctx.setLineDash(deposit.dash);
  ctx.lineDashOffset = deposit.dashOffset;
  ctx.stroke();
};

/**
 * Paints a continuous marker body with sparse, deterministic ink deposits.
 * @param {{ ctx: CanvasRenderingContext2D, pathLength?: number, seed: number, strokeWidth: number, tracePath: Function }} params
 * @returns {void}
 */
export const strokeMarkerPath = ({
  ctx,
  pathLength = 160,
  seed,
  strokeWidth,
  tracePath,
}) => {
  const baseAlpha = ctx.globalAlpha;
  const params = { pathLength, seed, strokeWidth, tracePath };
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  MARKER_STROKE_PASSES.forEach((pass, passIndex) => {
    paintMarkerPass({ baseAlpha, ctx, params, pass, passIndex });
  });
  ctx.restore();
};
